import { ReactNode } from "react";
import { Box, Skeleton } from "@mui/material";

import BotsTopBar from "./BotsTopBar";

/** Widths mirror the header pills (Action / Vendor / SSN / Browser profile). */
const META_PILL_WIDTHS = [128, 120, 132, 220];

/**
 * Placeholder for the major job workspace while the major job and its task jobs
 * load. Geometry follows the real panel so nothing jumps when data lands.
 */
export default function MajorJobWorkspaceSkeleton({
  switcher,
  stacked = false,
}: {
  switcher?: ReactNode;
  /** Phone layout: header, tabs and the job list, one under another. */
  stacked?: boolean;
}) {
  const pills = META_PILL_WIDTHS.map((w) => (
    <Skeleton key={w} component="li" variant="rounded" height={30} width={w} sx={{ borderRadius: "15px" }} />
  ));
  const listRows = Array.from({ length: stacked ? 4 : 6 }).map((_, i) => (
    <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
      <Skeleton variant="text" width="70%" sx={{ fontSize: 16 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.75 }}>
        <Skeleton variant="rounded" width={92} height={22} sx={{ borderRadius: "11px" }} />
        <Skeleton variant="rounded" height={6} sx={{ flex: 1 }} />
        <Skeleton variant="text" width={32} />
      </Box>
      <Skeleton variant="text" width={160} sx={{ ml: stacked ? 0 : "102px" }} />
    </Box>
  ));

  return (
    <Box
      aria-busy
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        ...(stacked ? {} : { flex: 1, minHeight: 0 }),
      }}
    >
      <BotsTopBar
        lead={switcher}
        title={<Skeleton variant="text" width={200} sx={{ fontSize: 16 }} />}
        chips={pills}
        stacked={stacked}
        actions={
          stacked ? undefined : (
            <>
              <Skeleton variant="rounded" width={84} height={36} />
              <Skeleton variant="rounded" width={36} height={36} />
            </>
          )
        }
      />
      <Box
        sx={{
          flex: "0 0 48px",
          display: "flex",
          alignItems: "center",
          gap: 4,
          px: stacked ? 2 : 4,
          borderTop: stacked ? 1 : 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Skeleton variant="text" width={72} />
        <Skeleton variant="text" width={64} />
        <Skeleton variant="text" width={88} />
      </Box>
      {stacked ? (
        <Box>{listRows}</Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
          <Box sx={{ flex: "0 1 500px", minWidth: 320, borderRight: 1, borderColor: "divider", overflow: "hidden" }}>
            <Box sx={{ height: 56, display: "flex", alignItems: "center", gap: 1.5, px: 2, borderBottom: 1, borderColor: "divider" }}>
              <Skeleton variant="text" width={96} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="rounded" width={132} height={36} />
            </Box>
            {listRows}
          </Box>
          <Box sx={{ flex: "1 1 560px", minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ flex: "0 1 360px" }}>
                <Skeleton variant="text" width="80%" sx={{ fontSize: 16 }} />
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Skeleton variant="rounded" width={170} height={24} />
                  <Skeleton variant="rounded" width={110} height={24} />
                </Box>
              </Box>
              <Box sx={{ flex: "1 1 260px" }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="rounded" height={8} sx={{ mt: 1 }} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, px: 3, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
              <Skeleton variant="rounded" width={170} height={32} />
              <Skeleton variant="rounded" width={110} height={36} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="rounded" width={90} height={36} />
              <Skeleton variant="rounded" width={120} height={36} />
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              <Box sx={{ border: 1, borderColor: "divider", borderRadius: "6px", px: 2, py: 1 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.85 }}>
                    <Skeleton variant="text" width={24} />
                    <Skeleton variant="rounded" width={88} height={22} />
                    <Skeleton variant="text" width={90} />
                    <Skeleton variant="text" sx={{ flex: 1 }} />
                    <Skeleton variant="rounded" width={200} height={32} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
